/**
 * Banner CTA action executor.
 *
 * The ctaUrl field on a UserBanner supports three formats:
 *   1. `/path`      — router.push to an internal route
 *   2. `modal:Name` — open a named modal (optionally `modal:Name?key=val`)
 *   3. `action:slug` — execute a registered action (see ACTIONS below)
 *   4. `https://...` — open a browser tab (web) or the default browser (native)
 *
 * This module centralises all CTA handling so every banner component
 * (PromoBanner, AnnouncementBanner, BannerModal, TooltipBannerLayer)
 * delegates to a single function instead of duplicating logic.
 */

import { Alert, Linking, Platform } from 'react-native';
import { URL, URLSearchParams } from 'react-native-url-polyfill';
import { useModalStore, useAuthStore } from '@/store';

// Per-user routes / modals that should open the auth CTA first when the
// banner fires for an anonymous visitor. Anything outside this set is
// treated as a public-friendly browse action.
const ANON_GATED_ACTION_SLUGS = new Set([
  'open_import',
  'open_memory',
  'open_referral',
  'open_subscription',
  'open_profile',
  'open_credits',
  'open_tasks',
  'open_usage',
  'open_file_upload',
  'open_credit_store',
]);
const ANON_GATED_MODAL_NAMES = new Set([
  'SettingsModalWeb',
  'SettingsModalMobile',
  'FileUploadModal',
  'CreateCollectionModal',
  'CustomAgentModal',
  'CreateMemoryModal',
  'PricingModal',
  'CreditsPurchaseModal',
  'MusicLyricsModal',
]);
const ANON_GATED_INTERNAL_ROUTES = new Set([
  '/files',
  '/collections',
  '/library',
  '/bookmarks',
  '/memory',
  '/tags',
  '/settings',
  '/referral',
  '/tools/history',
]);

function isAnonymous() {
  const { token, isTokenExpired } = useAuthStore.getState();
  return !token || isTokenExpired();
}

function openAuthCTA(openModal, ctx) {
  openModal('AuthCTAModal', {
    source: ctx?.source || 'banner_action',
    title: ctx?.title || 'Sign in to continue',
    subtitle: ctx?.subtitle,
    icon: ctx?.icon || 'lock',
  });
}

// ─── Action registry ─────────────────────────────────────────────────────────
// Each key is a slug used in admin (ctaUrl = "action:<slug>").
// The handler receives { router, openModal } and performs the action.

const ACTIONS = {
  // ── Settings deep-links ──────────────────────────────────────────────────
  open_import: ({ router, openModal }) => {
    if (Platform.OS === 'ios' || Platform.OS === 'android') {
      openModal('SettingsModalMobile', { initialScreen: 'import' });
    } else {
      openModal('SettingsModalWeb', { tab: 'import' });
    }
  },
  open_memory: ({ router, openModal }) => {
    if (Platform.OS === 'ios' || Platform.OS === 'android') {
      openModal('SettingsModalMobile', { initialScreen: 'memory' });
    } else {
      openModal('SettingsModalWeb', { tab: 'memory' });
    }
  },
  open_referral: ({ router, openModal }) => {
    if (Platform.OS === 'ios' || Platform.OS === 'android') {
      openModal('SettingsModalMobile', { initialScreen: 'referral' });
    } else {
      openModal('SettingsModalWeb', { tab: 'referral' });
    }
  },
  open_subscription: ({ router, openModal }) => {
    if (Platform.OS === 'ios' || Platform.OS === 'android') {
      openModal('SettingsModalMobile', { initialScreen: 'subscription' });
    } else {
      openModal('SettingsModalWeb', { tab: 'subscription' });
    }
  },
  open_profile: ({ router, openModal }) => {
    if (Platform.OS === 'ios' || Platform.OS === 'android') {
      openModal('SettingsModalMobile', { initialScreen: 'profile' });
    } else {
      openModal('SettingsModalWeb', { tab: 'profile' });
    }
  },
  open_credits: ({ router, openModal }) => {
    if (Platform.OS === 'ios' || Platform.OS === 'android') {
      openModal('SettingsModalMobile', { initialScreen: 'credits' });
    } else {
      openModal('SettingsModalWeb', { tab: 'credits' });
    }
  },
  open_tasks: ({ router, openModal }) => {
    if (Platform.OS === 'ios' || Platform.OS === 'android') {
      openModal('SettingsModalMobile', { initialScreen: 'tasks' });
    } else {
      openModal('SettingsModalWeb', { tab: 'tasks' });
    }
  },
  open_usage: ({ router, openModal }) => {
    if (Platform.OS === 'ios' || Platform.OS === 'android') {
      openModal('SettingsModalMobile', { initialScreen: 'usage' });
    } else {
      openModal('SettingsModalWeb', { tab: 'usage' });
    }
  },

  // ── Modals ───────────────────────────────────────────────────────────────
  open_file_upload: ({ openModal }) => {
    openModal('FileUploadModal');
  },
  open_pricing: ({ router, openModal, promoCode }) => {
    if (Platform.OS === 'ios' || Platform.OS === 'macos') {
      router.push('/(auth)/paywall');
    } else {
      openModal('PricingModal', promoCode ? { promoCode } : undefined);
    }
  },
  open_credit_store: ({ openModal, promoCode }) => {
    openModal('CreditsPurchaseModal', promoCode ? { promoCode } : undefined);
  },

  // ── Voice ────────────────────────────────────────────────────────────────
  start_voice: () => {
    const { requirePlusForVoice } = require('@/lib/requirePlusForVoice');
    if (!requirePlusForVoice({
      title: 'Plus required',
      subtitle: 'Voice Mode requires Gab AI Plus — it uses realtime audio and burns credits quickly.',
    })) return;
    const { useVoiceModeStore } = require('@/store');
    useVoiceModeStore.getState().actions.startVoiceMode(null);
  },

  // ── Navigation ───────────────────────────────────────────────────────────
  open_models: ({ router }) => { router.push('/models'); },
  open_agents: ({ router }) => { router.push('/agents'); },
  open_files:  ({ router }) => { router.push('/files'); },
  open_images: ({ router }) => { router.push('/images'); },
};

/**
 * Execute a banner's CTA.
 *
 * @param {object} banner — the banner object from useUserBanners
 * @param {object} deps   — { router, openModal }
 */
export function executeBannerCta(banner, { router, openModal }) {
  const { contentType, content, promoCode } = banner;

  const ctaUrl = content.ctaUrl;
  const anon = isAnonymous();

  // 1. Promo auto-apply: pass promo code to the target destination
  if (contentType === 'promo-code' && content.promoAutoApply && promoCode?.code) {
    if (ctaUrl && ctaUrl.startsWith('action:')) {
      const slug = ctaUrl.replace('action:', '');
      if (anon && ANON_GATED_ACTION_SLUGS.has(slug)) {
        openAuthCTA(openModal, {
          source: `banner_promo_${slug}`,
          title: 'Sign in to redeem this promo',
          subtitle: 'Promo codes apply to your account — create one or log in to claim this offer.',
          icon: 'tag',
        });
        return;
      }
      const handler = ACTIONS[slug];
      if (handler) {
        handler({ router, openModal, promoCode: promoCode.code });
        return;
      }
    }
    if (anon) {
      openAuthCTA(openModal, {
        source: 'banner_promo_pricing',
        title: 'Sign in to redeem this promo',
        subtitle: 'Promo codes apply to your account — create one or log in to claim this offer.',
        icon: 'tag',
      });
      return;
    }
    if (Platform.OS === 'ios' || Platform.OS === 'macos') {
      router.push('/(auth)/paywall');
    } else {
      openModal('PricingModal', { promoCode: promoCode.code });
    }
    return;
  }

  // 2. action: slug
  if (ctaUrl && ctaUrl.startsWith('action:')) {
    const slug = ctaUrl.replace('action:', '');
    if (anon && ANON_GATED_ACTION_SLUGS.has(slug)) {
      openAuthCTA(openModal, {
        source: `banner_action_${slug}`,
        title: 'Sign in to continue',
        subtitle: 'This action is tied to your account.',
        icon: 'lock',
      });
      return;
    }
    const handler = ACTIONS[slug];
    if (handler) {
      handler({ router, openModal });
    } else {
      console.warn(`[bannerActions] Unknown action slug: "${slug}"`);
    }
    return;
  }

  // 3. Internal route — anonymous visitors trying to deep-link into a
  // per-user route get the auth CTA. They can browse public routes
  // (agents/tools/models/etc.) freely.
  if (ctaUrl && ctaUrl.startsWith('/')) {
    if (anon && ANON_GATED_INTERNAL_ROUTES.has(ctaUrl)) {
      openAuthCTA(openModal, {
        source: `banner_route_${ctaUrl.replace(/\W+/g, '_')}`,
        title: 'Sign in to continue',
        subtitle: 'This page is tied to your account — create one or log in to access it.',
        icon: 'lock',
      });
      return;
    }
    router.push(ctaUrl);
    return;
  }

  // 4. modal:ModalName (optionally with ?key=val query params)
  if (ctaUrl && ctaUrl.startsWith('modal:')) {
    const raw = ctaUrl.replace('modal:', '');
    const qIdx = raw.indexOf('?');
    const modalName = qIdx === -1 ? raw : raw.substring(0, qIdx);
    if (anon && ANON_GATED_MODAL_NAMES.has(modalName)) {
      openAuthCTA(openModal, {
        source: `banner_modal_${modalName}`,
        title: 'Sign in to continue',
        subtitle: 'This action is tied to your account.',
        icon: 'lock',
      });
      return;
    }
    if (qIdx === -1) {
      openModal(raw);
    } else {
      const params = Object.fromEntries(new URLSearchParams(raw.substring(qIdx + 1)));
      openModal(modalName, params);
    }
    return;
  }

  // 5. External URL. Native banners use the system browser; previously their
  // CTA silently did nothing (or fell through to the promo paywall).
  if (ctaUrl && /^https?:\/\//i.test(ctaUrl)) {
    try {
      const url = new URL(ctaUrl);
      if (!url.hostname) return;
    } catch {
      return;
    }
    if (Platform.OS === 'web') {
      window.open(ctaUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    return Linking.openURL(ctaUrl).catch(() => {
      Alert.alert('Could not open link', `Please try again or open this address in your browser:\n${ctaUrl}`);
    });
  }

  // 6. Fallback for promo-code without auto-apply and no ctaUrl
  if (contentType === 'promo-code') {
    if (anon) {
      openAuthCTA(openModal, {
        source: 'banner_promo_fallback',
        title: 'Sign in to redeem this promo',
        subtitle: 'Promo codes apply to your account — create one or log in to claim this offer.',
        icon: 'tag',
      });
      return;
    }
    if (Platform.OS === 'ios' || Platform.OS === 'macos') {
      router.push('/(auth)/paywall');
    } else {
      openModal('PricingModal');
    }
  }
}

/** All registered action slugs — used for admin field reference docs. */
export const ACTION_SLUGS = Object.keys(ACTIONS);
